import React, { useState } from "react";
import { Download, ExternalLink } from "lucide-react";
import { useUiLanguage } from "./i18n/UiLanguageContext";
import { openStudentResourcePreview, StudentPreviewUnavailableError } from "./api";

type StudentResourceActionsProps = {
  url: string;
  filename: string;
  internal: boolean;
};

export default function StudentResourceActions({ url, filename, internal }: StudentResourceActionsProps) {
  const { t } = useUiLanguage();
  const [notice, setNotice] = useState("");
  const [opening, setOpening] = useState(false);

  async function openPreview() {
    setNotice("");
    setOpening(true);
    try {
      await openStudentResourcePreview(url);
    } catch (error) {
      if (error instanceof StudentPreviewUnavailableError) setNotice(t("studentFiles.previewUnavailable"));
      else setNotice(error instanceof Error ? error.message : t("studentFiles.openFailed"));
    } finally {
      setOpening(false);
    }
  }

  const actionClass = "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border px-3 text-xs font-black focus:outline-none focus:ring-4 focus:ring-sky-200";
  return <div className="mt-2">
    <div className="flex flex-wrap gap-2">
      {internal ? (
        <button type="button" onClick={() => void openPreview()} disabled={opening} aria-label={t("studentFiles.viewAria", { filename })} title={t("studentFiles.viewNewTab")} className={`${actionClass} border-emerald-700 bg-emerald-700 text-white disabled:cursor-wait disabled:opacity-70`}>
          <ExternalLink size={15} aria-hidden="true" />{opening ? t("studentFiles.opening") : t("studentFiles.view")}
        </button>
      ) : (
        <a href={url} target="_blank" rel="noopener noreferrer" aria-label={t("studentFiles.viewAria", { filename })} title={t("studentFiles.viewNewTab")} className={`${actionClass} border-emerald-700 bg-emerald-700 text-white`}>
          <ExternalLink size={15} aria-hidden="true" />{t("studentFiles.view")}
        </a>
      )}
      <a href={url} aria-label={t("studentFiles.downloadAria", { filename })} title={t("studentFiles.download")} className={`${actionClass} border-slate-200 bg-white text-slate-700`}>
        <Download size={15} aria-hidden="true" />{t("studentFiles.download")}
      </a>
    </div>
    {notice && <p role="status" className="mt-2 text-xs font-semibold text-amber-800">{notice}</p>}
  </div>;
}
